import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Checkbox } from "@libs/zapi-ui/ds/components/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@libs/zapi-ui/ds/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { BookIcon, ReportIcon } from "@libs/zapi-ui/assets";
import { CheckIcon, XIcon } from "@phosphor-icons/react";
import { ChevronDown } from "lucide-react";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import type { TExportProjectOptions } from "../lib/pdf-export";

type TDocType = TExportProjectOptions["docType"];
type TReportType = TExportProjectOptions["reportType"];

type TExportOptionsModalProps = {
  open: boolean;
  onClose: () => void;
  onExport: (options: TExportProjectOptions) => void;
  isExporting: boolean;
  projectName: string;
};

function sanitizeFileName(value: string) {
  return value.replace(/[^\p{L}\p{N}_\-\s]/gu, "");
}

type TReportTypeCardProps = {
  title: string;
  subtitle: string;
  icon: ReactNode;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  testId: string;
};

function ReportTypeCard({ title, subtitle, icon, selected, disabled, onClick, testId }: TReportTypeCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`flex items-start gap-2 rounded-lg border p-3 text-left ${
        selected ? "border-innoveraZinc-950" : "border-innoveraZinc-150 hover:border-innoveraZinc-300"
      }`}
    >
      <div
        className={`flex size-8 shrink-0 items-center justify-center rounded-md ${
          selected ? "bg-innoveraZinc-950 text-white" : "bg-innoveraZinc-100 text-innoveraZinc-900"
        }`}
      >
        {icon}
      </div>
      <div className="flex flex-col gap-1">
        <FigmaTypography variant="label-2" color="zinc900">
          {title}
        </FigmaTypography>
        <FigmaTypography variant="body-3" color="zinc500">
          {subtitle}
        </FigmaTypography>
      </div>
    </button>
  );
}

type TIncludeCheckboxProps = {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
};

function IncludeCheckbox({ id, label, checked, disabled, onChange }: TIncludeCheckboxProps) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onChange(value)}
        disabled={disabled}
        data-testid={id}
        className="size-[18px] border"
      />
      <label htmlFor={id} className="cursor-pointer">
        <FigmaTypography variant="body-2" color="zinc900">
          {label}
        </FigmaTypography>
      </label>
    </div>
  );
}

export function ExportOptionsModal({ open, onClose, onExport, isExporting, projectName }: TExportOptionsModalProps) {
  const { t } = useTranslation(["projectHome", "common"]);
  const { isLabs } = useMeStore();
  const { selectedProject } = useProjectStore();
  const isTemplateLess = selectedProject?.projectTemplateSlug === "";
  const [fileName, setFileName] = useState("");
  const [docType, setDocType] = useState<TDocType>("pdf");
  const [reportType, setReportType] = useState<TReportType>("full");
  const [includeDecisionFrame, setIncludeDecisionFrame] = useState(false);
  const [includeProjectInstructions, setIncludeProjectInstructions] = useState(false);
  const [includeAnalysisAndIntelligence, setIncludeAnalysisAndIntelligence] = useState(false);
  const [includeVault, setIncludeVault] = useState(false);

  useEffect(() => {
    if (open) {
      setFileName(sanitizeFileName(projectName).trim());
      setDocType("pdf");
      setReportType("full");
      setIncludeDecisionFrame(false);
      setIncludeProjectInstructions(false);
      setIncludeAnalysisAndIntelligence(false);
      setIncludeVault(false);
    }
  }, [open, projectName]);

  const docTypes: TDocType[] = isLabs ? ["pdf", "docx", "md"] : ["pdf", "docx"];

  const handleExport = () => {
    onExport({
      fileName: fileName.trim() || "report",
      docType: isTemplateLess ? "pdf" : docType,
      reportType,
      includeDecisionFrame,
      includeProjectInstructions,
      includeAnalysisAndIntelligence: isTemplateLess ? false : includeAnalysisAndIntelligence,
      includeVault
    });
  };

  const handleClose = () => {
    if (!isExporting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[537px] gap-5 p-5" showCloseButton={false} closeLabel={t("common:close")}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="font-medium">{t("common:export")}</DialogTitle>
            <DialogClose
              data-testid="export-options-close-button"
              className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden disabled:pointer-events-none"
              disabled={isExporting}
            >
              <XIcon className="size-5" />
              <span className="sr-only">{t("exportOptions.close")}</span>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="export-filename">
              <FigmaTypography variant="body-2" color="zinc900" className="leading-4">
                {t("common:fileName")}
              </FigmaTypography>
            </label>
            <div className="flex h-9 items-stretch overflow-hidden rounded-lg border border-innoveraZinc-150 focus-within:border-innoveraZinc-300">
              <div className="relative flex flex-1 flex-col gap-2">
                <Input
                  id="export-filename"
                  className="h-full flex-1 rounded-none border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  value={fileName}
                  onChange={(e) => setFileName(sanitizeFileName(e.target.value))}
                  placeholder="report"
                  disabled={isExporting}
                  data-testid="export-filename-input"
                />
              </div>
              {isTemplateLess ? (
                <div
                  data-testid="export-format-pdf"
                  className="flex items-center border-l border-innoveraZinc-150 bg-innoveraZinc-50 px-3"
                >
                  <span className="text-sm font-normal text-innoveraZinc-900">.pdf</span>
                </div>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    data-testid="export-format-trigger"
                    disabled={isExporting}
                    render={
                      <button
                        type="button"
                        className="flex items-center gap-1 border-l border-innoveraZinc-150 bg-innoveraZinc-50 px-3"
                      />
                    }
                  >
                    <span className="text-sm font-normal text-innoveraZinc-900">.{docType}</span>
                    <ChevronDown className="size-4 text-innoveraZinc-900" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" sideOffset={8} className="w-auto min-w-48 rounded-xl p-1.5">
                    {docTypes.map((type) => (
                      <DropdownMenuItem
                        key={type}
                        data-testid={`export-format-${type}`}
                        onClick={() => setDocType(type)}
                      >
                        <CheckIcon size={14} className={docType === type ? "opacity-100" : "opacity-0"} />
                        <FigmaTypography variant="label-2">.{type}</FigmaTypography>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <FigmaTypography variant="body-2" color="zinc900" className="leading-4">
              {t("exportOptions.type")}
            </FigmaTypography>
            <div className="grid grid-cols-2 gap-2">
              <ReportTypeCard
                title={t("exportOptions.fullReport")}
                subtitle={isTemplateLess ? t("exportOptions.fullReportPages") : t("exportOptions.fullReportChapters")}
                icon={<BookIcon />}
                selected={reportType === "full"}
                disabled={isExporting}
                onClick={() => setReportType("full")}
                testId="export-type-full"
              />
              <ReportTypeCard
                title={t("exportOptions.summary")}
                subtitle={t("exportOptions.summaryDescription")}
                icon={<ReportIcon />}
                selected={reportType === "executive"}
                disabled={isExporting}
                onClick={() => setReportType("executive")}
                testId="export-type-executive"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2.5">
            <FigmaTypography variant="body-2" color="zinc900" className="leading-4">
              {t("exportOptions.include")}
            </FigmaTypography>
            <div className="flex flex-col gap-1.5">
              <IncludeCheckbox
                id="include-decision-frame"
                label={t("exportOptions.projectFrame")}
                checked={includeDecisionFrame}
                disabled={isExporting}
                onChange={setIncludeDecisionFrame}
              />
              {isLabs && (
                <IncludeCheckbox
                  id="include-project-instructions"
                  label={t("exportOptions.projectInstructions")}
                  checked={includeProjectInstructions}
                  disabled={isExporting}
                  onChange={setIncludeProjectInstructions}
                />
              )}
              {!isTemplateLess && (
                <IncludeCheckbox
                  id="include-analysis-intelligence"
                  label={t("exportOptions.analysisQuestions")}
                  checked={includeAnalysisAndIntelligence}
                  disabled={isExporting}
                  onChange={setIncludeAnalysisAndIntelligence}
                />
              )}
              <IncludeCheckbox
                id="include-vault"
                label={t("exportOptions.vaultFilesAndLinks")}
                checked={includeVault}
                disabled={isExporting}
                onChange={setIncludeVault}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            data-testid="export-options-cancel-button"
            onClick={handleClose}
            disabled={isExporting}
          >
            {t("common:cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleExport}
            disabled={isExporting || !fileName.trim()}
            data-testid="export-submit"
          >
            {isExporting ? (
              <>
                <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
                {t("common:exporting")}
              </>
            ) : (
              t("common:export")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
